<script lang="ts">
export default { name: 'PublishDiffModal' };
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from 'reka-ui';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import UIButton from '@/components/ui/UIButton.vue';
import DiffView from '@/components/prompt/DiffView.vue';
import { roundTripSchema } from '@/utilities/schemaRoundTrip';
import type { Prompt } from '@/types/ui/PromptManager';

interface SectionChanged {
  outputFormat: boolean;
  system: boolean;
  userPrompt: boolean;
}

interface DraftPrompt {
  expectedOutputSchema: string;
  systemPrompt: string;
  userPrompt: string;
}

const props = defineProps<{
  open: boolean;
  draftPrompt: DraftPrompt;
  publishedPrompt: Prompt;
  sectionChanged: SectionChanged;
}>();

const emit = defineEmits<{
  'update:open': [value: boolean];
}>();

const isOpen = computed({
  get: () => props.open,
  set: (value) => emit('update:open', value),
});

const activeTab = ref<'output-format' | 'system-instructions' | 'prompt-text'>('output-format');

const pickInitialTab = () => {
  if (props.sectionChanged.outputFormat) return 'output-format';
  if (props.sectionChanged.system) return 'system-instructions';
  if (props.sectionChanged.userPrompt) return 'prompt-text';
  return 'output-format';
};

watch(
  () => props.open,
  (open) => {
    if (open) activeTab.value = pickInitialTab();
  },
  { immediate: true },
);

// Round-trip both sides through parse → generate so the diff only surfaces
// real content changes, not serialization format evolution.
const schemaOriginal = computed(() => {
  const raw = props.publishedPrompt.expectedOutputSchema || '';
  return roundTripSchema(raw) ?? raw;
});

const schemaModified = computed(() => {
  const raw = props.draftPrompt.expectedOutputSchema || '';
  return roundTripSchema(raw) ?? raw;
});
</script>

<template>
  <Dialog v-model:open="isOpen">
    <DialogContent class="flex h-[85vh] max-w-[95vw] flex-col bg-white text-slate-900 sm:max-w-[95vw]">
      <DialogHeader>
        <DialogTitle>Changes since v{{ publishedPrompt.version }}</DialogTitle>
      </DialogHeader>

      <TabsRoot
        v-model="activeTab"
        class="flex min-h-0 flex-1 flex-col">
        <TabsList class="inline-flex h-10 w-fit items-center rounded-md bg-slate-100 p-1 text-slate-600">
          <TabsTrigger
            value="output-format"
            class="inline-flex items-center justify-center gap-2 rounded-sm px-3 py-1.5 text-sm font-medium whitespace-nowrap ring-offset-white transition-all focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-slate-950 data-[state=active]:shadow-xs">
            Output Format
            <span
              v-if="sectionChanged.outputFormat"
              class="relative flex h-2 w-2"
              title="Changed">
              <span class="absolute inline-flex h-full w-full animate-pulse rounded-full bg-amber-400 opacity-30"></span>
              <span class="relative inline-flex h-2 w-2 rounded-full bg-amber-500"></span>
            </span>
          </TabsTrigger>
          <TabsTrigger
            value="system-instructions"
            class="inline-flex items-center justify-center gap-2 rounded-sm px-3 py-1.5 text-sm font-medium whitespace-nowrap ring-offset-white transition-all focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-slate-950 data-[state=active]:shadow-xs">
            System Instructions
            <span
              v-if="sectionChanged.system"
              class="relative flex h-2 w-2"
              title="Changed">
              <span class="absolute inline-flex h-full w-full animate-pulse rounded-full bg-amber-400 opacity-30"></span>
              <span class="relative inline-flex h-2 w-2 rounded-full bg-amber-500"></span>
            </span>
          </TabsTrigger>
          <TabsTrigger
            value="prompt-text"
            class="inline-flex items-center justify-center gap-2 rounded-sm px-3 py-1.5 text-sm font-medium whitespace-nowrap ring-offset-white transition-all focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-slate-950 data-[state=active]:shadow-xs">
            Prompt Text
            <span
              v-if="sectionChanged.userPrompt"
              class="relative flex h-2 w-2"
              title="Changed">
              <span class="absolute inline-flex h-full w-full animate-pulse rounded-full bg-amber-400 opacity-30"></span>
              <span class="relative inline-flex h-2 w-2 rounded-full bg-amber-500"></span>
            </span>
          </TabsTrigger>
        </TabsList>

        <TabsContent
          value="output-format"
          class="mt-3 min-h-0 flex-1 rounded-md border border-slate-200">
          <DiffView
            v-if="sectionChanged.outputFormat"
            :original="schemaOriginal"
            :modified="schemaModified"
            language="json" />
          <div
            v-else
            class="flex h-full items-center justify-center text-sm text-slate-500">
            No changes in this section
          </div>
        </TabsContent>

        <TabsContent
          value="system-instructions"
          class="mt-3 min-h-0 flex-1 rounded-md border border-slate-200">
          <DiffView
            v-if="sectionChanged.system"
            :original="publishedPrompt.systemPrompt || ''"
            :modified="draftPrompt.systemPrompt"
            language="plain" />
          <div
            v-else
            class="flex h-full items-center justify-center text-sm text-slate-500">
            No changes in this section
          </div>
        </TabsContent>

        <TabsContent
          value="prompt-text"
          class="mt-3 min-h-0 flex-1 rounded-md border border-slate-200">
          <DiffView
            v-if="sectionChanged.userPrompt"
            :original="publishedPrompt.userPrompt || ''"
            :modified="draftPrompt.userPrompt"
            language="plain" />
          <div
            v-else
            class="flex h-full items-center justify-center text-sm text-slate-500">
            No changes in this section
          </div>
        </TabsContent>
      </TabsRoot>

      <DialogFooter>
        <UIButton
          variant="outline"
          @click="isOpen = false">
          Close
        </UIButton>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
